import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLanguage } from "../../lang/helpers";
import { VisionStatementInterface } from "../DevelopmentPlan/interfaces";
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import InformationCircleIcon from "../../components/Icons/InformationCircleIcon";
import PencilIcon from "../../components/Icons/PencilIcon";
import Api from "../../services/Api";
import { StrArr, StrMap } from "../../interfaces/common";


interface Props {
  visionStatement: VisionStatementInterface;
  save: Function;
  setEdit: Function;
  showDetailModal: Function;
}

interface FeedbackQuestion {
  id: string;
  question_id?: string;
  name: string;
  translationsName?: Record<string, string>;
  scoreSelf?: number;
  scoreOther?: number;
  scoreOtherNormalized?: number;
  scoreSelfNormalized?: number;
  scoreWeighted?: number;
  scoreList?: number[];
  gap?: number;
  freeTexts?: string[];
  spreadRespondentsNormalized?: number;
}
interface QuestionInterface {
  questionId: string;
  names: StrMap;
  noAnswer: boolean;
  freeText: boolean;
  answer: number;
  freeAnswer: string;
}

interface SectionInterface {
  names: StrMap;
  questions: Array<QuestionInterface>;
}

interface SurveyInterface {
  language: string;
  languages: string[];
  deadline: string;
  answerOptions: number;
  answerType: number;
  showNoAnswer: boolean;
  translations_answers: StrArr;
  names: StrMap;
  sections: Array<SectionInterface>
}

interface AnswerOptionInterface {
  nr: number;
  option: string;
}

const SelfAssessmentSurveyResponse = (props: Props) => {
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const api = Api.getInstance();
  const [feedbackQuestions, setFeedbackQuestions] = useState<Array<FeedbackQuestion>>([]);
  const [feedbackAnswerOptions, setFeedbackAnswerOptions] = useState<Array<AnswerOptionInterface>>([]);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const focusAreaQuestions =
      props.visionStatement.surveyRequests[0].surveyResult.focusAreas[0].questions;
    const _lang = props.visionStatement.surveyRequests[0].surveyResult.languages[0];
    const answerOptionsByLang =
      props.visionStatement.surveyRequests[0].surveyResult.answerOptions[_lang];

    setFeedbackQuestions(focusAreaQuestions);
    const _answerOptions: Array<AnswerOptionInterface> = [];
    for (let i = answerOptionsByLang.length; i > 0; i--) {
      _answerOptions.push({
        nr: i,
        option: answerOptionsByLang[i],
      })
    }
    setFeedbackAnswerOptions(_answerOptions);
    setLoading(false);
  }, [props.visionStatement]);

  if (loading) {
    return <div className="p-8 text-gray-300">Loading...</div>;
  }

  const allQuestionsAnswered = () => {
    return feedbackQuestions.every((_, index) => answers[index] !== undefined);
  };

  const sendResponses = () => {
    const surveyRequest = props.visionStatement.surveyRequests[0];
    const surveyResult = surveyRequest.surveyResult;

    const questions: QuestionInterface[] = feedbackQuestions.map((question, index) => {
      const selectedOption = answers[index];
      const answerIndex = selectedOption;

      return {
        questionId: question.question_id ?? question.id,
        names: { [lang]: question.name },
        noAnswer: false,
        freeText: false,
        answer: answerIndex,
        freeAnswer: "",
      };
    });

    const updatedSurvey: SurveyInterface = {
      language: lang,
      languages: surveyResult.languages,
      deadline: surveyRequest.deadline,
      answerOptions: surveyResult.nrAnswerOptions,
      answerType: 0,
      showNoAnswer: true,
      names: { [lang]: props.visionStatement.title },
      translations_answers: surveyResult.answerOptions,
      sections: [
        {
          names: { [lang]: "" },
          questions,
        },
      ],
    };

    api.post('client/survey/' + props.visionStatement.surveyRequests[0].id, updatedSurvey).then((response) => {
      if (response.json.status === "ok") {
        props.save();
      } else {
        alert("Error submitting responses");
      }
    }).catch((err) => {
      console.error("sendResponses error:", err);
      alert("Network error submitting responses");
    });
  }

  const colWidth = 100 / (feedbackAnswerOptions.length - 1) + "%";

  return (
    <div className="main-content m-4">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="z-heading1 text-white">
              {t("self-assessment")}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <CustomButton
              buttonType="transparentBase"
              size="sm"
              onClick={() => props.showDetailModal()}
              leftIcon={<InformationCircleIcon className="h-4 w-4" alt="info" />}
              style={{ width: "unset" }}
            >
              {t("long-term-goal-details")}
            </CustomButton>
            <CustomButton
              buttonType="transparentBase"
              size="sm"
              onClick={() => props.setEdit()}
              leftIcon={<PencilIcon variant="white" className="h-4 w-4" alt="edit" />}
              style={{ width: "unset" }}
            >
              {t("btn-edit-questions")}
            </CustomButton>
          </div>
        </div>
      </div>
      <div className="mb-6">
        {t('surveyIntro')}
      </div>
      <hr className="mt-1 border-indigo-700 mb-6" />
      <div>
        {feedbackQuestions.map((question, index) => (
          <div key={"feedback_question_" + index} className="mb-6">
            <div className="z-badge transparent text-gray-400">
              # {index + 1}
            </div>
            <p className="text-gray-400 mt-2">{question.name}</p>

            <div className="mt-2 flex justify-between items-end gap-x-4 border-b border-indigo-700 pb-1">
              {feedbackAnswerOptions.filter((a, i) => i > 0).map((option, optIndex) => (
                <div key={"fqt" + index + "_opt" + optIndex} className="text-center" style={{width: colWidth}}>{option.option}</div>
              ))}
            </div>
            <div className="mt-4 flex justify-between gap-4 border-b border-indigo-700 pb-4">
              {feedbackAnswerOptions.filter((a, i) => i > 0).map((option, optIndex) => (
                <div className="flex justify-center" key={"fq" + index + "_opt" + optIndex} style={{width: colWidth}}>
                <RadioButton
                  id={"feedback_question_" + index + "_opt_" + optIndex}
                  name={"feedback_question_" + index}
                  value={option.option}
                  label=""
                  checked={answers[index] === option.nr}
                  onChange={() => setAnswers((prev) => ({ ...prev, [index]: option.nr }))}
                />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <CustomButton
            buttonType="blueBase"
            onClick={() => sendResponses()}
            style={{ width: "unset" }}
            disabled={!allQuestionsAnswered()}
        >
          {t("submit")}
        </CustomButton>
      </div>

    </div>
  );
};

export default SelfAssessmentSurveyResponse;